import {useFormik} from 'formik';
import axios from 'axios';
import Form from 'react-bootstrap/Form';
import {useAlert} from '@/components/alerts';
import useData from '@/utils/useData';
import CellLoader from '@/components/loading/CellLoader';
import {useStoreActions} from 'easy-peasy';
import i18n from 'i18n';
import {BaseSettingsFormProps} from './types';
import {buildURL} from '@/utils/urlBuilder';
import {extractErrorMessage} from '@/utils/errorHandling';

// Helper function to map resource URLs to URL builder patterns
const getDetailUrlFromResource = (resourceUrl: string, entityId: number | string): string => {
    // Map common resource URLs to their corresponding detail URL patterns
    const resourceMappings: Record<string, string> = {
        [window.URLS.storiesResource]: 'storyDetails',
        [window.URLS.storyboardsResource]: 'storyboardDetails',
        [window.URLS.actionsResource]: 'actionDetails',
        [window.URLS.rulesResource]: 'ruleDetails',
        [window.URLS.teamsResource]: 'teamDetails',
        [window.URLS.usersResource]: 'userDetails',
        [window.URLS.agentsResource]: 'agentDetails',
        [window.URLS.customersListResource]: 'customerDetails',
        [window.URLS.notificationsResource]: 'notificationDetails',
        [window.URLS.evaluationsResource]: 'evaluationDetails',
        [window.URLS.audioAnonymizationTasksResource]: 'audioAnonymizationTaskDetails',
        [window.URLS.transcriptionTasksResource]: 'transcriptionTaskDetails',
        [window.URLS.configurationsResource]: 'configurationDetails',
        [window.URLS.emailFetchTasksResource]: 'emailFetchTaskDetails',
        [window.URLS.emailFetchCredentialsResource]: 'emailFetchCredentialDetails',
        [window.URLS.emailFetchAccountsResource]: 'emailFetchAccountDetails',
        [window.URLS.dictionariesResource]: 'dictionaryDetails',
    };

    const urlPattern = resourceMappings[resourceUrl];
    if (urlPattern) {
        // Determine the parameter name based on the pattern
        const paramMappings: Record<string, string> = {
            'storyDetails': 'story_id',
            'storyboardDetails': 'storyboard_id',
            'actionDetails': 'action_id',
            'ruleDetails': 'rule_id',
            'teamDetails': 'team_id',
            'userDetails': 'user_id',
            'agentDetails': 'agent_id',
            'customerDetails': 'customer_id',
            'notificationDetails': 'obj_id',
            'evaluationDetails': 'obj_id',
            'audioAnonymizationTaskDetails': 'task_id',
            'transcriptionTaskDetails': 'task_id',
            'configurationDetails': 'configuration_id',
            'emailFetchTaskDetails': 'task_id',
            'emailFetchCredentialDetails': 'credential_id',
            'emailFetchAccountDetails': 'account_id',
            'dictionaryDetails': 'dictionary_id',
        };

        const paramName = paramMappings[urlPattern];
        if (paramName) {
            return buildURL(urlPattern, {[paramName]: entityId});
        }
    }

    // Fallback to old behavior if no mapping found
    return `${resourceUrl}/${entityId}`;
};

const BaseSettingsForm = <T extends Record<string, any>>({
    entityId,
    resourceUrl,
    storeActionPath,
    validationSchema,
    initialValues,
    successMessageKey,
    entityIdPropName = 'objId',
    useDetailFetch = true,
    enableReinitialize = true,
    onSave = () => { },
    customErrorHandling,
    customSuccessHandling,
    customSubmissionHandler,
    additionalStoreActions = [],
    children,
}: BaseSettingsFormProps<T>) => {
    const {showSuccess} = useAlert();

    // Construct detail URL for fetching existing data
    const shouldFetchDetail = useDetailFetch && !!entityId && !!resourceUrl;
    const detailUrl = shouldFetchDetail ? getDetailUrlFromResource(resourceUrl, entityId) : null;

    // Fetch existing data if editing (and detail fetching is enabled)
    const [existingData, loadingData] = useData<T>(detailUrl, {
        useLocalStore: true,
    });


    // Store actions
    const setStoreData = useStoreActions((actions: any) => {
        if (!storeActionPath) {return null;}
        const pathParts = storeActionPath.split('.');
        let current = actions;
        for (const part of pathParts) {
            current = current?.[part];
        }
        return current;
    });

    const setActiveStoryObjectDataReload = useStoreActions(
        (actions: any) => actions.activeStoryObjectDetails?.setReload
    );

    // Additional store actions
    const additionalActions = additionalStoreActions.map(({path}) =>
        useStoreActions((actions: any) => {
            const pathParts = path.split('.');
            let current = actions;
            for (const part of pathParts) {
                current = current[part];
            }
            return current;
        })
    );

    const loading = shouldFetchDetail && loadingData;

    const onSubmit = async (values: T) => {
        try {
            let response: any;
            let actionName: string;

            // Use custom submission handler if provided
            if (customSubmissionHandler) {
                response = await customSubmissionHandler(values, formik);
                actionName = 'processed'; // Generic action name for custom handlers
            } else {
                // Default CRUD submission logic
                let requestMethod: (url: string, data: T) => Promise<any>;
                let url = resourceUrl;

                if (entityId) {
                    url = getDetailUrlFromResource(resourceUrl, entityId);
                    requestMethod = axios.put;
                    actionName = 'changed';
                } else {
                    requestMethod = axios.post;
                    actionName = 'created';
                }

                response = await requestMethod(url, values);
            }

            // Run success handling
            if (customSuccessHandling) {
                // Let the caller handle any custom side effects (store updates, navigation, toasts, etc.)
                customSuccessHandling(response, actionName);
            } else {
                // Default success handling
                if (storeActionPath && setStoreData) {
                    setStoreData(response.data);
                }

                // Execute additional store actions
                additionalStoreActions.forEach(({value}, index) => {
                    additionalActions[index](value);
                });

                // Reload active story object if needed
                if (setActiveStoryObjectDataReload) {
                    setActiveStoryObjectDataReload(true);
                }
            }

            // Show success toast if a message key is provided and no custom success handler is used
            if (successMessageKey && !customSuccessHandling) {
                // Pass both "action" and "actionName" for flexibility in translation templates
                showSuccess(i18n.t(successMessageKey, {
                    actionName,
                    action: actionName,
                }));
            }

            onSave();
        } catch (e: any) {
            // Custom error handling
            if (customErrorHandling && customErrorHandling(e, formik)) {
                return;
            }

            // Default error handling
            if (e.response?.status === 400) {
                const errorData = e.response?.data;

                if (errorData?.errors && typeof errorData.errors === 'object') {
                    Object.keys(errorData.errors).forEach(field => {
                        formik.setFieldError(field, errorData.errors[field]);
                    });
                } else if (errorData?.error) {
                    const errorField = errorData?.key || Object.keys(values)[0];
                    formik.setFieldError(errorField, errorData.error);
                } else if (errorData?.message) {
                    const errorField = errorData?.key || Object.keys(values)[0];
                    formik.setFieldError(errorField, errorData.message);
                } else {
                    const errorMessage = extractErrorMessage(e, 'Validation failed');
                    formik.setFieldError(Object.keys(values)[0], errorMessage);
                }
                return;
            }

            throw e;
        }
    };

    const formik = useFormik<T>({
        initialValues: existingData || initialValues,
        validationSchema,
        enableReinitialize,
        onSubmit,
    });


    if (loading) {
        return <CellLoader />;
    }

    return (
        <Form noValidate onSubmit={formik.handleSubmit}>
            {children(formik, !!loading)}
        </Form>
    );
};

export default BaseSettingsForm;
